import React, { useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function LeaderForm({ leader, onClose, onSaved }) {
  const [form, setForm] = useState({ full_name: leader?.full_name || '', email: leader?.email || '', role_title: leader?.role_title || '', active: leader?.active !== false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const titleRef = useRef(null);
  const openerRef = useRef(typeof document === 'undefined' ? null : document.activeElement);

  async function submit(event) {
    event.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.role_title.trim()) {
      setError('Enter a name, email address and role or job title.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Enter a valid email address, such as name@example.com.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const values = { ...form, full_name: form.full_name.trim(), email: form.email.trim(), role_title: form.role_title.trim() };
      if (leader) await base44.entities.SeniorLeader.update(leader.id, values);
      else await base44.entities.SeniorLeader.create({ ...values, accuracy_score: 0, questions_answered: 0, questions_correct: 0 });
      onSaved(values.full_name, Boolean(leader));
    } catch (err) {
      setError(err.message || 'The SLT member was not saved. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent returnFocusRef={openerRef} className="max-h-[90vh] overflow-y-auto rounded-2xl" onOpenAutoFocus={(event) => { event.preventDefault(); titleRef.current?.focus(); }}>
        <DialogHeader><DialogTitle ref={titleRef} tabIndex={-1}>{leader ? 'Edit leader' : 'Add leader'}</DialogTitle><DialogDescription>Enter the member’s contact details. All fields are required.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div><label htmlFor="leader-name" className="mb-1 block text-sm font-medium">Full name</label><input id="leader-name" required value={form.full_name} onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))} className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground" /></div>
          <div><label htmlFor="leader-email" className="mb-1 block text-sm font-medium">Email address</label><input id="leader-email" type="email" required value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground" /></div>
          <div><label htmlFor="leader-role" className="mb-1 block text-sm font-medium">Role or job title</label><input id="leader-role" required value={form.role_title} onChange={(event) => setForm((current) => ({ ...current, role_title: event.target.value }))} className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground" /></div>
          {error && <p role="alert" className="accessible-error rounded-lg border border-red-500 bg-background p-3 text-sm text-foreground">{error}</p>}
          {saving && <p role="status" className="text-sm text-muted-foreground">Saving SLT member…</p>}
          <DialogFooter className="gap-3 sm:space-x-0"><DialogClose asChild><button type="button" disabled={saving} className="min-h-11 rounded-lg border border-border px-4 py-2 font-medium">Cancel</button></DialogClose><button type="submit" disabled={saving} className="min-h-11 rounded-lg bg-teal-700 px-5 py-2 font-semibold text-white disabled:opacity-50">{saving ? 'Saving…' : leader ? 'Save changes' : 'Add member'}</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
